import { useEffect, useMemo, useState } from 'react'

import {
  BRAND_REFRESH_DEBUG_UPDATED_EVENT,
  readBrandRefreshDebugOverrides,
} from './brandRefreshBetaDebug'
import { getCountdownFromStartedAt } from './brandRefreshCountdown'
import { useBrandRefreshActivationData } from './useBrandRefreshActivationData'

export interface BrandRefreshBannerCountdownState {
  daysRemaining: number
  isExpired: boolean
  startedAt: string | null
  /** False while global-beta countdown waits on activation_data (avoids premature auto opt-in). */
  isReady: boolean
}

/**
 * 7-day countdown from `activation_data.brand_refresh_beta.banner_started_at`.
 * Dev overrides via localStorage — see `brandRefreshBetaDebug.ts` and `BrandRefreshBetaDebugControls`.
 * Active in local dev/review or with `?debug=true` on staging/production.
 */
export function useBrandRefreshBannerCountdown(
  isVisible: boolean,
  useServerCountdown: boolean,
): BrandRefreshBannerCountdownState {
  const { blob, isLoading } = useBrandRefreshActivationData({ skip: !useServerCountdown })
  const [debugRevision, setDebugRevision] = useState(0)

  useEffect(() => {
    if (!isVisible) {
      return undefined
    }

    const refreshDebug = () => setDebugRevision((value) => value + 1)

    const onStorage = (event: StorageEvent) => {
      if (event.key?.startsWith('brand_refresh_beta_debug')) {
        refreshDebug()
      }
    }

    window.addEventListener(BRAND_REFRESH_DEBUG_UPDATED_EVENT, refreshDebug)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(BRAND_REFRESH_DEBUG_UPDATED_EVENT, refreshDebug)
      window.removeEventListener('storage', onStorage)
    }
  }, [isVisible])

  const debug = useMemo(
    () => readBrandRefreshDebugOverrides(debugRevision),
    [debugRevision],
  )

  return useMemo(() => {
    if (!isVisible) {
      return {
        daysRemaining: debug.countdownDays,
        isExpired: false,
        startedAt: null,
        isReady: true,
      }
    }

    if (!useServerCountdown) {
      return {
        daysRemaining: debug.countdownDays,
        isExpired: debug.forceExpired,
        startedAt: debug.startedAt,
        isReady: true,
      }
    }

    if (isLoading) {
      return {
        daysRemaining: debug.countdownDays,
        isExpired: false,
        startedAt: null,
        isReady: false,
      }
    }

    const serverStartedAt = blob.banner_started_at
    const startedAt = debug.startedAt ?? serverStartedAt ?? null
    const countdown = getCountdownFromStartedAt(startedAt, {
      countdownDays: debug.countdownDays,
      forceExpired: debug.forceExpired,
    })

    return {
      ...countdown,
      isReady: Boolean(startedAt) || debug.forceExpired || debug.startedAt != null,
    }
  }, [blob.banner_started_at, debug, isLoading, isVisible, useServerCountdown])
}
